import { Component, OnInit } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { Observable, firstValueFrom } from 'rxjs';
import { AukceImage } from '../models/aukceImage';
import { AsyncPipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { IonicModule, AlertController, ToastController } from "@ionic/angular";
import { UserInfo } from '../models/userInfo';
import { downloadOutline } from 'ionicons/icons';
import { addIcons } from 'ionicons';

addIcons({
  'download-outline': downloadOutline,
});

@Component({
  selector: 'app-admin',
  templateUrl: './admin.page.html',
  styleUrls: ['./admin.page.scss'],
  imports: [
    AsyncPipe,
    JsonPipe,
    IonicModule,
    NgForOf,
    NgIf
  ],
  standalone: true
})
export class AdminPage implements OnInit {
  isAdmin = false
  currentPhotos: Observable<AukceImage[]>
  userInfoCache: { [email: string]: UserInfo } = {}

  constructor(
    private authService: AuthService,
    private dataService: DataService,
    private alertController: AlertController,
    private toastController: ToastController
  ) {
    this.isAdmin = this.authService.isAdmin
    this.currentPhotos = this.dataService.getPhotos()
  }

  ngOnInit() {
    // Preload user info for faster name lookup
    this.preloadUserInfo();
  }

  async preloadUserInfo() {
    try {
      const userInfoSnapshot = await firstValueFrom(
        this.dataService.getAllUserInfo()
      );
      
      userInfoSnapshot.forEach(doc => {
        const userData = doc.data();
        this.userInfoCache[userData.email] = userData;
      });
    } catch (error) {
      console.error('Error preloading user info:', error);
    }
  }

  priceHistoryLength(p: AukceImage) {
    return Object.keys(p.priceHistory || {}).length
  }

  getSortedPrices(p: AukceImage): number[] {
    return Object.keys(p.priceHistory || {})
      .map(Number)
      .sort((a, b) => b - a);
  }

  getUserNameByEmail(email: string): string {
    // First check the cache
    if (this.userInfoCache[email]) {
      return this.userInfoCache[email].name;
    }
    
    // Fallback to email if no name found
    return email;
  }

  getUserFullNameByEmail(email: string): string {
    // First check the cache
    if (this.userInfoCache[email]) {
      const user = this.userInfoCache[email];
      // Combine first and last name, fallback to nickname if no full name
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      return fullName || user.name || email;
    }
    
    // Fallback to email if no user found
    return email;
  }

  async removeLatestBid(photo: AukceImage) {
    if (!this.isAdmin) return;

    const alert = await this.alertController.create({
      header: 'Odstranit poslední příhoz',
      message: `Opravdu chcete odstranit poslední příhoz pro dílo "${photo.title}"?`,
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel'
        },
        {
          text: 'Odstranit',
          handler: async () => {
            try {
              await this.dataService.removeLatestBid(photo.id);
              const toast = await this.toastController.create({
                message: 'Poslední příhoz byl úspěšně odstraněn',
                duration: 2000,
                color: 'success'
              });
              await toast.present();
            } catch (error) {
              console.error('Error removing latest bid:', error);
              const toast = await this.toastController.create({
                message: 'Nepodařilo se odstranit příhoz',
                duration: 2000,
                color: 'danger'
              });
              await toast.present();
            }
          }
        }
      ]
    });

    await alert.present();
  }

  async exportToCSV() {
    try {
      const photos = await firstValueFrom(this.currentPhotos);
      
      // Prepare CSV data
      const csvData = [
        // CSV header
        ['Dílo', 'Autor', 'Aktuální cena', 'Nejvyšší nabídka', 'Email', 'Počet příhozů'].join(','),
        // CSV rows
        ...photos.map(p => [
          `"${p.title}"`,
          `"${p.author}"`,
          p.currentPrice,
          `"${p.currentTopper ? this.getUserFullNameByEmail(p.currentTopper.email) : ''}"`,
          `"${p.currentTopper ? p.currentTopper.email : ''}"`,
          this.priceHistoryLength(p)
        ].join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `aukce-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const toast = await this.toastController.create({
        message: 'Export byl úspěšně stažen',
        duration: 2000,
        color: 'success'
      });
      toast.present();
    } catch (error) {
      console.error('Export failed:', error);
      const toast = await this.toastController.create({
        message: 'Při exportu došlo k chybě',
        duration: 2000,
        color: 'danger'
      });
      toast.present();
    }
  }
}
